import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    Icon,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    useColorModeValue,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import React from 'react';
import { MdOutlineMoreHoriz } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { translate } from '../../../i18n';
import { buildCapacityPricingLossWarning } from '../../../utils/capacity-pricing-warnings';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useDeleteCourt } from '../hooks/useDeleteCourt';
import { useDuplicateCourt } from '../hooks/useDuplicateCourt';
import { useGetCourt } from '../hooks/useGetCourt';
import { classifyRead } from '../../_shared/readState';

type Props = { courtId: number; };
const CourtActionMenu = ({ courtId }: Props) => {
    const { isOpen: isDialogOpen, onOpen: onDialogOpened, onClose: onDialogClosed } = useDisclosure();
    const { selectedLocation } = useSelectedLocation();
    const { mutateAsync: deleteAsync, isLoading: isDeleting } = useDeleteCourt(courtId, selectedLocation?.id);
    // This read's ONLY job in this component is to gate the Duplicate action,
    // which is what made a refused one so quiet: `handleDuplicate` opened with
    // a bare `if (!court) return;`, so a 403/404/5xx on `GET courts/:id` turned
    // an enabled menu item into a no-op that said nothing at all — no toast, no
    // error, no disabled state — for the whole session, because the query
    // carries `staleTime`/`cacheTime: Infinity`. That is D64's shape one step
    // out: the guard is present and correct, and its arm is silence.
    const { data: court, isError: isCourtError, isInitialLoading: isCourtInitialLoading } = useGetCourt(courtId);
    const courtReadState = classifyRead(isCourtError, isCourtInitialLoading, court);
    const navigate = useNavigate();
    const toast = useToast();

    const { mutateAsync: duplicateAsync, isLoading: isDuplicating } = useDuplicateCourt(courtId, (newCourt) => {
        navigate(`/admin/courtDetails?court=${newCourt.id}`);
    });

    const iconColor = useColorModeValue('brand.500', 'white');
    const bgButton = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
    const bgHover = useColorModeValue({ bg: 'secondaryGray.400' }, { bg: 'whiteAlpha.50' });
    const bgFocus = useColorModeValue({ bg: 'secondaryGray.300' }, { bg: 'whiteAlpha.100' });

    const cancelRef = React.useRef<HTMLButtonElement>(null);

    const handleDelete = async () => {
        try {
            const result = await deleteAsync();
            onDialogClosed();
            // Archiving is the widest version of the loss D136 reports: EVERY
            // sport this court priced leaves the sellable set at once, so a
            // size the venue still offers can become unbookable everywhere with
            // no upcoming booking to trip the guards above. There is no success
            // toast on this rail; only a loss speaks.
            const warning = buildCapacityPricingLossWarning(result);
            if (warning) {
                toast({
                    title: translate('courts.capacity_pricing_lost_title'),
                    description: warning,
                    status: 'warning',
                    duration: 10000,
                    isClosable: true,
                });
            }
        } catch (error: any) {
            onDialogClosed();
            toast({
                title: translate('courts.delete_court_error'),
                description: error?.message || translate('error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleDuplicate = async () => {
        // A read that did not come back is stated, not swallowed. The two
        // non-answered states get different affordances on purpose: `loading`
        // is transient and disables the item below (clicking it is impossible,
        // and it resolves), while `unreadable`/`never-issued` never resolve on
        // their own, so the operator is told — with the same toast a failed
        // duplicate already uses, rather than a new i18n key (R96-7's freeze).
        if (courtReadState !== 'answered' || !court) {
            toast({
                title: translate('courts.duplicate_error'),
                description: translate('common.unexpected_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return;
        }
        const toastId = toast({
            title: translate('courts.duplicating'),
            status: 'loading',
            duration: null,
            isClosable: false,
        });
        try {
            await duplicateAsync();
            toast.update(toastId, {
                title: translate('courts.duplicate_success'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error: any) {
            toast.update(toastId, {
                title: translate('courts.duplicate_error'),
                description: error?.message || translate('error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <>
            <Menu>
                <MenuButton
                    alignItems='center'
                    justifyContent='center'
                    bg={bgButton}
                    _hover={bgHover}
                    _focus={bgFocus}
                    _active={bgFocus}
                    w='37px'
                    h='37px'
                    lineHeight='100%'

                    borderRadius='10px'
                >
                    <Icon as={MdOutlineMoreHoriz} color={iconColor} w='24px' h='24px' />
                </MenuButton>
                <MenuList>
                    <MenuItem
                        onClick={handleDuplicate}
                        isDisabled={isDuplicating || courtReadState === 'loading'}
                    >
                        {translate('common.duplicate')}
                    </MenuItem>
                    {/* Disabling delete for now */}
                    <MenuItem onClick={onDialogOpened}>{translate('delete')}</MenuItem>
                </MenuList>
            </Menu>

            <AlertDialog
                isOpen={isDialogOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDialogClosed}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {translate('delete')}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {translate('courts.delete_court_confirmation_message')}
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDialogClosed}>
                                {translate('cancel')}
                            </Button>
                            <Button colorScheme="red" onClick={handleDelete} isLoading={isDeleting} ml={3}>
                                {translate('delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default CourtActionMenu;